<script lang="ts" setup>
import { useMutation, useQuery } from "@tanstack/vue-query"
import { toTypedSchema } from "@vee-validate/zod"
import { useUrlSearchParams } from "@vueuse/core"
import { useFieldArray, useForm } from "vee-validate"
import { computed, ref, watchEffect } from "vue"
import { useRouter } from "vue-router"
import { z } from "zod"
import { createCustomTemplate, getCustomTemplate, updateCustomTemplate } from "@/api"
import { Footer } from "@/components"
import { Button, FileInput, Hint, Icon, Input, Textarea } from "@/components/ui"
import { useTranslation } from "@/composables"
import { EventTemplate } from "@/models"

const props = withDefaults(defineProps<{ templateId?: number }>(), {
  templateId: undefined,
})

const emit = defineEmits<{
  (e: "save"): void
  (e: "success"): void
  (e: "error", err?: unknown): void
}>()

const { t } = useTranslation()
const router = useRouter()

const queryString = useUrlSearchParams<{ step?: number, hostAccountId?: number }>()

const isFormInitialized = ref(false)

const ACCEPTED_IMAGE_EXTENSIONS = [".pjpeg", ".pjp", ".jfif", ".jpg", ".jpeg", ".png", ".gif", ".bmp", ".webp"] as const

const imageFileSchema = z
  .instanceof(File, { message: t("template_details_form.form_error.img_type") })
  .refine(file => ACCEPTED_IMAGE_EXTENSIONS.some(extension =>
    file.name.toLowerCase().endsWith(extension),
  ), {
    message: t("template_details_form.form_error.img_type"),
  })

const validationSchema = toTypedSchema(
  z.object({
    name: z.string().min(1, t("template_details_form.form_error.min_name")).max(25, t("template_details_form.form_error.max_name")),
    description: z.string().max(250, t("template_details_form.form_error.max_description")).optional(),
    facebook: z.union([z.string().url(), z.literal("")]).optional(),
    instagram: z.union([z.string().url(), z.literal("")]).optional(),
    youtube: z.union([z.string().url(), z.literal("")]).optional(),
    tiktok: z.union([z.string().url(), z.literal("")]).optional(),
    pinterest: z.union([z.string().url(), z.literal("")]).optional(),
    linkedIn: z.union([z.string().url(), z.literal("")]).optional(),
    x: z.union([z.string().url(), z.literal("")]).optional(),
    headerImage: z.optional(imageFileSchema).nullable(),
    footerImage: z.optional(imageFileSchema).nullable(),
    customLinks: z
      .array(
        z.object({
          key: z.string().min(1, t("template_details_form.form_error.custom_link_key")),
          value: z.union([z.string().url(), z.literal("")]).optional(),
        }),
      )
      .optional(),
  }),
)

const { handleSubmit, values, setFieldValue } = useForm({
  validationSchema,
  initialValues: {
    name: undefined,
    description: undefined,
    facebook: undefined,
    instagram: undefined,
    youtube: undefined,
    tiktok: undefined,
    pinterest: undefined,
    linkedIn: undefined,
    x: undefined,
    customLinks: [],
  },
})

const { fields, push, remove } = useFieldArray("customLinks")

function initialiseFormValues(template: EventTemplate) {
  if (isFormInitialized.value)
    return

  const socialLinks = template?.customFooterLinks?.socialLinks
  const footerLinks = template?.customFooterLinks?.footerLinks

  setFieldValue("name", template?.name ?? "")
  setFieldValue("description", template?.description ?? "")
  setFieldValue("facebook", socialLinks?.facebook ?? "")
  setFieldValue("instagram", socialLinks?.instagram ?? "")
  setFieldValue("youtube", socialLinks?.youtube ?? "")
  setFieldValue("tiktok", socialLinks?.tiktok ?? "")
  setFieldValue("pinterest", socialLinks?.pinterest ?? "")
  setFieldValue("linkedIn", socialLinks?.linkedIn ?? "")
  setFieldValue("x", socialLinks?.x ?? "")
  setFieldValue("customLinks", footerLinks?.length
    ? footerLinks as Array<{ key: string, value?: string | undefined }>
    : [])

  isFormInitialized.value = true
}

function clearField(fieldName: keyof typeof values) {
  setFieldValue(fieldName, undefined)
}

const { data: customTemplate, isLoading: isLoadingTemplate } = useQuery({
  queryKey: ["getCustomTemplate", props.templateId],
  queryFn: () => getCustomTemplate(props.templateId),
  enabled: !!props.templateId,
})

const { mutate: createTemplate, isPending: isPendingCreate } = useMutation({
  mutationFn: (customTemplate: EventTemplate) => createCustomTemplate(customTemplate, queryString.hostAccountId),
  onSuccess: async (template) => {
    const redirect = queryString.hostAccountId
      ? `/template/${template.id}/edit?hostAccountId=${queryString.hostAccountId}&step=2`
      : `/template/${template.id}/edit?step=2`

    router.push(redirect)
  },
  onError: (err) => {
    emit("error", err)
  },
})

const { mutate: updateTemplate, isPending: isUpdatingTemplate } = useMutation({
  mutationFn: (customTemplate: EventTemplate) => updateCustomTemplate(customTemplate),
  onSuccess: async () => {
    emit("success")
  },
  onError: (err) => {
    emit("error", err)
  },
})

watchEffect(() => {
  if (customTemplate.value)
    initialiseFormValues(customTemplate.value)
})

const isLoading = computed(() => isPendingCreate.value || isLoadingTemplate.value || isUpdatingTemplate.value)

const footerLinksPreview = computed(() => {
  return {
    socialLinks: {
      facebook: values.facebook || "",
      instagram: values.instagram || "",
      youtube: values.youtube || "",
      tiktok: values.tiktok || "",
      pinterest: values.pinterest || "",
      linkedIn: values.linkedIn || "",
      x: values.x || "",
    },
    footerLinks: values.customLinks || null,
  }
})

const footerLogoPreview = computed(() => {
  if (values.footerImage && values.footerImage instanceof File) {
    return URL.createObjectURL(values.footerImage)
  }

  return values.footerImage === null ? "" : customTemplate.value?.pathToFooterImage ?? ""
})

const onSubmit = handleSubmit((values) => {
  emit("save")

  const template = new EventTemplate({
    ...customTemplate.value,
    name: values.name,
    description: values.description,
    headerImage: values.headerImage,
    pathToHeaderImage: values.headerImage === null ? undefined : customTemplate.value?.pathToHeaderImage,
    footerImage: values.footerImage,
    pathToFooterImage: values.footerImage === null ? undefined : customTemplate.value?.pathToFooterImage,
    customFooterLinks: {
      socialLinks: {
        facebook: values.facebook || "",
        instagram: values.instagram || "",
        youtube: values.youtube || "",
        tiktok: values.tiktok || "",
        pinterest: values.pinterest || "",
        linkedIn: values.linkedIn || "",
        x: values.x || "",
      },
      footerLinks: values.customLinks || null,
    },
  })

  customTemplate.value ? updateTemplate(template) : createTemplate(template)
})
</script>

<template>
  <div>
    <div class="flex flex-col gap-4 w-full mb-10">
      <div class="flex flex-col gap-2 mb-4">
        <h1 class="text-xl font-comfortaa font-bold">
          {{ customTemplate ? t('template_details_form.update_template_title') : t('template_details_form.create_template_title') }}
        </h1>
        <div class="text-label">
          {{ t('template_details_form.template_details_message') }}
        </div>
      </div>

      <transition name="gpx-fade">
        <div v-if="isLoading" class="flex flex-col gap-4">
          <div class="gpx-skeleton h-10" />
          <div class="gpx-skeleton h-10" />
          <div class="gpx-skeleton h-[400px]" />
        </div>
        <div v-else>
          <form @submit.prevent="onSubmit">
            <div class="flex flex-col gap-10">
              <div class="flex flex-col gap-4">
                <h3 class="font-comfortaa font-bold text-xl">
                  {{ t('template_details_form.template_details_header') }}
                </h3>
                <Input :label="t('template_details_form.template_name_label')" name="name" clearable @clear="clearField('name')" />
                <Textarea :label="t('template_details_form.template_description_label')" name="description" />
              </div>

              <!-- Custom Logos -->
              <div class="flex flex-col gap-4">
                <h3 class="font-comfortaa font-bold text-xl">
                  {{ t('template_details_form.custom_logo_header') }}
                </h3>

                <div class="flex flex-wrap gap-4">
                  <div>
                    <h4 class="font-comfortaa font-bold text-lg mb-2">
                      {{ t('template_details_form.header_img_header') }}
                    </h4>
                    <Hint class="mb-4" :hint="t('template_details_form.header_img_hint')" />
                    <FileInput
                      name="headerImage"
                      preview-type="image"
                      :accept="ACCEPTED_IMAGE_EXTENSIONS.join(',')"
                      :preview="customTemplate?.pathToHeaderImage"
                      :accept-message="t('event_details_form.form.image_accept')"
                    />
                  </div>

                  <div>
                    <h4 class="font-comfortaa font-bold text-lg mb-2">
                      {{ t('template_details_form.footer_img_header') }}
                    </h4>
                    <Hint class="mb-4" :hint="t('template_details_form.footer_img_hint')" />
                    <FileInput
                      name="footerImage"
                      preview-type="image"
                      :accept="ACCEPTED_IMAGE_EXTENSIONS.join(',')"
                      :preview="customTemplate?.pathToFooterImage"
                      :accept-message="t('event_details_form.form.image_accept')"
                    />
                  </div>
                </div>
              </div>

              <div class="flex flex-col gap-4">
                <h3 class="font-comfortaa font-bold text-xl">
                  {{ t('template_details_form.custom_footer_header') }}
                </h3>

                <!-- Social Media -->
                <div class="grow flex flex-col gap-4">
                  <h4 class="font-comfortaa font-bold text-lg">
                    {{ t('template_details_form.social_media_header') }}
                  </h4>
                  <Hint :hint="t('template_details_form.social_media_hint')" />
                  <Input label="Facebook" name="facebook" label-icon="Facebook" clearable @clear="clearField('facebook')" />
                  <Input label="Instagram" name="instagram" label-icon="Instagram" clearable @clear="clearField('instagram')" />
                  <Input label="YouTube" name="youtube" label-icon="YouTube" clearable @clear="clearField('youtube')" />
                  <Input label="TikTok" name="tiktok" label-icon="TikTok" clearable @clear="clearField('tiktok')" />
                  <Input label="Pinterest" name="pinterest" label-icon="Pinterest" clearable @clear="clearField('pinterest')" />
                  <Input label="LinkedIn" name="linkedIn" label-icon="LinkedIn" clearable @clear="clearField('linkedIn')" />
                  <Input label="X" name="x" label-icon="X" clearable @clear="clearField('x')" />
                </div>

                <!-- Custom Links -->
                <h4 class="font-comfortaa font-bold text-lg mt-4">
                  {{ t('template_details_form.custom_links_header') }}
                </h4>
                <Hint :hint="t('template_details_form.custom_links_hint')" />
                <div v-for="(link, index) in fields" :key="link.key" class="flex items-center gap-2 w-full">
                  <div class="grow">
                    <Input :name="`customLinks.${index}.key`" :label="t('template_details_form.custom_key_label')" />
                  </div>
                  <div class="grow">
                    <Input :name="`customLinks.${index}.value`" :label="t('template_details_form.custom_value_label')" />
                  </div>
                  <div class="shrink-0">
                    <Button variant="flat" type="button" @click="remove(index)">
                      <Icon icon="Trash" />
                    </Button>
                  </div>
                </div>
                <Button class="w-fit" variant="tertiary" @click.prevent="push({ key: '', value: '' })">
                  {{ t('template_details_form.add_link_button') }}
                </Button>
              </div>
              <hr>
              <div>
                <div class="flex gap-1 font-semibold font-comfortaa items-center mb-2">
                  <Icon icon="EyeOn" />
                  {{ t('template_details_form.footer_preview_header') }}
                </div>
                <Footer class="border" :custom-logo="footerLogoPreview" :custom-footer-links="footerLinksPreview" hide-locale-switch />
              </div>
            </div>

            <div class="flex items-center flex-wrap mt-16 gap-2">
              <Button variant="secondary" prefix-icon="ArrowLeft" :disabled="isPendingCreate" @click.prevent="() => router.back()">
                {{ t('back') }}
              </Button>
              <Button class="w-fit px-10" :disabled="isPendingCreate" @click.prevent="onSubmit">
                {{ t('save_and_continue') }}
              </Button>
            </div>
          </form>
        </div>
      </transition>
    </div>
  </div>
</template>
